import * as React from "react";
import * as PropertiesSelector from "../../../properties-selector";
import * as LanguageTexts from "@genesys/shared/lib/language-texts";
import * as CaseSettingsOverride from "../../components/settings-overrides";
import styled from "styled-components";
import { PropertyFilter, PropertyValueSet } from "@genesys/property";
import {
  PropertyDefinitions,
  PropertyDefinition,
  mergeTopLevelCaseOverridesWithMainSettings,
  definitionIsValid
} from "../../tools";
import { State } from "./state";
import { Props, InternalProps } from "./types";
import {
  SystemOperationTime,
  SystemConfig,
  AddtionalBuildingInfo,
  BuildingInfo,
  InfiltrationModelSelections,
  AdditionalSelections
} from "./components";

const Container = styled.div``;

const BuildingContainer = styled.div`
  margin-bottom: 15px;
`;

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export function View(props: Props) {
  const {
    sharedState,
    propertySelectorState,
    currentSettingsOverrides,
    propertyDefinitions,
    isDisabled,
    caseSettingsOverrideDispatch,
    propertieSelectorDispatch
  } = props;

  const renderPropertySelector = (
    propertyDefinitions: PropertyDefinitions,
    customRowRenderer?: PropertiesSelector.RowRenderer,
    fieldGroup: string = "MoistureLoadTool.SystemConfiguration"
  ) => {
    return (
      <PropertiesSelector.PropertiesSelectorView
        dispatch={propertieSelectorDispatch}
        productId={""}
        sharedState={sharedState}
        translatePropertyName={propertyName =>
          sharedState.translate(LanguageTexts.globalPropertyName(propertyName))
        }
        translatePropertyValue={(propertyName, propertyValue) =>
          sharedState.translate(
            LanguageTexts.globalPropertyName(propertyName + "_" + propertyValue)
          )
        }
        propertiesInfo={propertyDefinitions}
        readonlyProperties={propertyDefinitions.reduce(
          (definitionsSoFar, current) => {
            if (
              current.enabled_filter &&
              !PropertyFilter.isValid(
                PropertiesSelector.getSelectedProperties(propertySelectorState),
                current.enabled_filter
              )
            ) {
              definitionsSoFar.push(current.name);
            }
            return definitionsSoFar;
          },
          [] as Array<string>
        )}
        customRowRenderer={customRowRenderer}
        fieldGroup={fieldGroup}
        isReadonly={isDisabled}
        showCodes={false}
        state={propertySelectorState}
      />
    );
  };

  const renderSystemOverride = (
    overridableProperties: ReadonlyArray<string>,
    fieldGroup: string = "MoistureLoadTool.SystemConfiguration"
  ) => {
    return (
      <CaseSettingsOverride.View
        useAsPop
        fieldGroups={fieldGroup}
        dispatch={caseSettingsOverrideDispatch}
        sharedState={sharedState}
        caseOverrides={mergeTopLevelCaseOverridesWithMainSettings(
          PropertiesSelector.getSelectedProperties(propertySelectorState),
          currentSettingsOverrides
        )}
        isDisabled={isDisabled}
        overridableProperties={overridableProperties.reduce(
          (soFar: Array<PropertyDefinition>, propertyName) => {
            const property = propertyDefinitions.find(
              property => property.name === propertyName
            );
            if (property !== undefined) {
              return soFar.concat(property);
            }
            return soFar;
          },
          []
        )}
      />
    );
  };

  return (
    <Container>
      <FlexContainer>
        <LeftContent
          {...props}
          renderPropertySelector={renderPropertySelector}
          renderSystemOverride={renderSystemOverride}
        />
        <MiddleContent
          {...props}
          renderPropertySelector={renderPropertySelector}
          renderSystemOverride={renderSystemOverride}
        />
        <RightContent
          {...props}
          renderPropertySelector={renderPropertySelector}
          renderSystemOverride={renderSystemOverride}
        />
      </FlexContainer>
    </Container>
  );
}

function LeftContent(props: InternalProps) {
  return (
    <div>
      <SystemConfig {...props} />
      <SystemOperationTime {...props} />
      <InfiltrationModelSelections {...props} />
    </div>
  );
}

function MiddleContent(props: InternalProps) {
  return (
    <div>
      <AdditionalSelections {...props} />
    </div>
  );
}

function RightContent(props: InternalProps) {
  return (
    <BuildingContainer>
      <BuildingInfo {...props} />
      <AddtionalBuildingInfo {...props} />
    </BuildingContainer>
  );
}

export function GetErrorSeverity(
  state: State,
  currentMoistureLoadSettings: PropertyValueSet.PropertyValueSet
): "none" | "warning" | "error" {
  const propertyNames = PropertyValueSet.getPropertyNames(
    currentMoistureLoadSettings
  );

  const topLevelPropertyDefinitions = state.propertyDefinitions!.filter(x =>
    propertyNames.includes(x.name)
  );

  const hasInvalidRefs = !topLevelPropertyDefinitions.every(definition =>
    definitionIsValid(definition, currentMoistureLoadSettings)
  );

  return hasInvalidRefs ? "error" : "none";
}
